import { LoadingOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";
import React from "react";

interface AIButtonProps {
    onClick: () => void;
    disabled?: boolean;
    loading?: boolean;
    tooltipTitle?: string;
    disabledTooltipTitle?: string;
    buttonText: string;
    icon?: React.ReactNode;
}

const AIButton: React.FC<AIButtonProps> = ({ onClick, disabled = false, loading = false, tooltipTitle = "", disabledTooltipTitle = "", buttonText, icon }) => {
    const isDisabled = disabled || loading;

    const getTooltipTitle = () => {
        if (disabled && disabledTooltipTitle) {
            return disabledTooltipTitle;
        }
        if (loading) {
            return "Processing...";
        }
        return tooltipTitle;
    };

    const defaultIcon = (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 48 48" className="relative z-10">
            <path
                fill={isDisabled ? "#64748b" : "#babffc"}
                d="M36.845,45.378l-0.494-1.132c-0.881-2.019-2.467-3.627-4.447-4.506l-1.522-0.676	c-0.823-0.366-0.823-1.562,0-1.928l1.437-0.639c2.03-0.902,3.645-2.569,4.511-4.657l0.507-1.224c0.354-0.853,1.533-0.853,1.886,0	l0.507,1.224c0.866,2.088,2.481,3.755,4.511,4.657l1.437,0.639c0.823,0.366,0.823,1.562,0,1.928	C45.178,39.064,41.286,38.15,36.845,45.378z"
            />
            <path
                fill={isDisabled ? "#475569" : "#324561"}
                d="M37.799,45.979c-0.133,0-0.269-0.027-0.398-0.083c-0.506-0.221-0.738-0.81-0.518-1.316l0.493-1.132	c1.085-2.485,3.027-4.45,5.468-5.534l1.521-0.676c0.505-0.226,1.096,0.003,1.32,0.507c0.224,0.505-0.003,1.096-0.508,1.32	l-1.521,0.676c-1.983,0.881-3.562,2.481-4.447,4.506l-0.492,1.131C38.553,45.754,38.186,45.979,37.799,45.979z"
            />
            <path
                fill={isDisabled ? "#64748b" : "#babffc"}
                d="M22.285,31.088l-1.16,4.493c-0.675,1.547-2.816,1.547-3.491,0l-1.757-4.024	c-1.564-3.581-4.378-6.432-7.888-7.99L3.153,21.42c-1.538-0.682-1.538-2.919,0-3.602l4.685-2.08	c3.601-1.598,6.465-4.554,8.002-8.258l1.78-4.288c0.66-1.591,2.859-1.591,3.52,0l1.78,4.288c1.537,3.703,4.402,6.659,8.002,8.258	l4.685,2.08c1.538,0.682,1.538,2.919,0,3.602l-5.433,1.678C26.663,24.657,23.849,27.507,22.285,31.088z"
            />
            <path
                fill={isDisabled ? "#475569" : "#324561"}
                d="M20.208,36.182c-0.133,0-0.269-0.027-0.399-0.084c-0.506-0.221-0.737-0.811-0.516-1.316l1.757-4.024	c1.768-4.049,4.932-7.252,8.909-9.018l4.836-2.147c0.511-0.227,1.096,0.004,1.32,0.508c0.224,0.505-0.004,1.096-0.508,1.32	l-4.836,2.147c-3.521,1.562-6.321,4.4-7.89,7.99l-1.757,4.024C20.961,35.957,20.594,36.182,20.208,36.182z"
            />
        </svg>
    );

    return (
        <Tooltip title={getTooltipTitle()} classNames={{ root: "font-semibold" }}>
            <button
                type="button"
                onClick={onClick}
                disabled={isDisabled}
                className={`relative inline-flex h-12 overflow-hidden rounded-full border-none p-1 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 focus:ring-offset-slate-50 ${
                    isDisabled ? "cursor-not-allowed" : "cursor-pointer"
                }`}
            >
                <span
                    className={`absolute inset-[-1000%] ${
                        loading
                            ? "animate-[spin_1s_linear_infinite] bg-[conic-gradient(from_90deg_at_50%_50%,#22d3ee_0%,#6366f1_50%,#22d3ee_100%)]"
                            : isDisabled
                              ? "animate-[spin_2s_linear_infinite] bg-[conic-gradient(from_90deg_at_50%_50%,#64748b_0%,#334155_50%,#64748b_100%)]"
                              : "animate-[spin_2s_linear_infinite] bg-[conic-gradient(from_90deg_at_50%_50%,#E2CBFF_0%,#393BB2_50%,#E2CBFF_100%)]"
                    }`}
                />

                <span
                    className={`inline-flex h-full w-full items-center justify-center rounded-full px-6 py-2 text-sm font-medium backdrop-blur-3xl ${
                        isDisabled ? "bg-slate-600 text-slate-300" : "bg-slate-950 text-white"
                    }`}
                >
                    <div className="flex items-center gap-2.5">
                        {!isDisabled && <div className="absolute -inset-0.5 h-10 w-10 animate-ping rounded-full bg-indigo-400 opacity-30"></div>}

                        {loading ? <LoadingOutlined className="relative z-10 text-cyan-400" style={{ fontSize: "24px" }} spin /> : icon || defaultIcon}

                        <span
                            className={`relative z-10 text-base font-bold tracking-wide drop-shadow-lg ${
                                loading
                                    ? "bg-gradient-to-r from-cyan-400 via-blue-500 to-indigo-600 bg-clip-text text-transparent"
                                    : "bg-gradient-to-r from-purple-400 via-blue-500 to-indigo-600 bg-clip-text text-transparent"
                            }`}
                        >
                            {buttonText}
                        </span>
                    </div>
                </span>
            </button>
        </Tooltip>
    );
};

export default React.memo(AIButton);
